import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, List, ListItem, Typography } from '@mui/material';
import { AttachFile, Close } from '@mui/icons-material';
import SoftButton from 'components/SoftButton';
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  ATTACHMENT_ACCEPT,
  formatFileSize,
  getFileExtension,
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_SIZE_MB,
  MAX_ATTACHMENTS,
} from '../const/cancellationAttachments';

const path = 'POLICY_CANCELLATION.ATTACHMENTS';

const isSameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const AttachmentsInput = ({ files, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [errors, setErrors] = useState([]);

  const handleSelect = (event) => {
    const selected = Array.from(event.target.files ?? []);
    // Reset so the same file can be selected again after removing it.
    event.target.value = '';

    const nextErrors = [];
    const accepted = [...files];
    selected.forEach((file) => {
      if (accepted.some((existing) => isSameFile(existing, file))) {
        return;
      }
      if (
        !ALLOWED_ATTACHMENT_EXTENSIONS.includes(getFileExtension(file.name))
      ) {
        nextErrors.push(t(`${path}.invalidType`, { name: file.name }));
      } else if (file.size < 1 || file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        nextErrors.push(
          t(`${path}.invalidSize`, {
            name: file.name,
            size: MAX_ATTACHMENT_SIZE_MB,
          }),
        );
      } else if (accepted.length >= MAX_ATTACHMENTS) {
        nextErrors.push(t(`${path}.tooMany`, { max: MAX_ATTACHMENTS }));
      } else {
        accepted.push(file);
      }
    });

    setErrors([...new Set(nextErrors)]);
    onChange(accepted);
  };

  const handleRemove = (file) => {
    setErrors([]);
    onChange(files.filter((existing) => existing !== file));
  };

  return (
    <Box sx={{ mb: 2, maxWidth: '40rem' }} data-testid="AttachmentsInput">
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {t(`${path}.title`)}
        <span aria-hidden="true"> *</span>
      </Typography>
      <Typography
        variant="body2"
        color="grey.700"
        sx={{ fontSize: '0.85rem', mb: 1 }}
      >
        {t(`${path}.helper`, {
          max: MAX_ATTACHMENTS,
          size: MAX_ATTACHMENT_SIZE_MB,
        })}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={ATTACHMENT_ACCEPT}
        onChange={handleSelect}
      />
      <SoftButton
        type="button"
        variant="outlined"
        color="info"
        size="small"
        disabled={disabled || files.length >= MAX_ATTACHMENTS}
        onClick={() => inputRef.current?.click()}
      >
        <AttachFile sx={{ mr: 0.5 }} fontSize="small" />
        {t(`${path}.add`)}
      </SoftButton>
      {files.length > 0 && (
        <List dense sx={{ mt: 1 }}>
          {files.map((file) => (
            <ListItem
              key={`${file.name}-${file.size}-${file.lastModified}`}
              disableGutters
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={t(`${path}.remove`)}
                  disabled={disabled}
                  onClick={() => handleRemove(file)}
                >
                  <Close fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body2">
                {file.name}{' '}
                <Typography component="span" variant="body2" color="grey.600">
                  ({formatFileSize(file.size)})
                </Typography>
              </Typography>
            </ListItem>
          ))}
        </List>
      )}
      {errors.map((error) => (
        <Typography
          key={error}
          variant="caption"
          color="error"
          sx={{ display: 'block' }}
        >
          {error}
        </Typography>
      ))}
    </Box>
  );
};

AttachmentsInput.propTypes = {
  files: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default AttachmentsInput;
